/**
 * GWP (Gift With Purchase) cart block.
 * Single file: custom element for cart page + shared UI used by minicart and cart page.
 */

import { h, render, Fragment } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { formatMoney, globalState } from '../helpers/utils'
import { emitterProps, globalEmitter } from '../helpers/emitter-event'
import { useGwp } from '../hooks/useGwp'
import { useFlickity } from '../hooks/useFlickity'
import { CartConfigKeys } from '../hooks/useCartGlobal'
import type { ProcessedGiftWithPurchaseObject } from '../helpers/gwp.types'
import { ResultUpsellCard } from '../libs/athos/components/ResultUpsellCard'
import Icon from 'snippets/icon/icon'

// --- Helpers ---

/** Maps progress percentage (0–100) to bar width for space-around marker positions */
export function progressBarWidthForSpaceAround(percentage: number, n: number): number {
  if (n <= 0) return 0
  if (n === 1) return Math.min(percentage, 100)
  if (percentage >= 100) return 100
  const p = Math.min(percentage, 100)
  const segmentIndex = Math.min(Math.floor((p * n) / 100), n - 1)
  const segmentStartOld = (segmentIndex * 100) / n
  const segmentEndOld = ((segmentIndex + 1) * 100) / n
  const segmentStartNew = segmentIndex === 0 ? 0 : ((segmentIndex - 0.5) / n) * 100
  const segmentEndNew = ((segmentIndex + 0.5) / n) * 100
  const t = segmentEndOld - segmentStartOld > 0 ? (p - segmentStartOld) / (segmentEndOld - segmentStartOld) : 1
  return segmentStartNew + t * (segmentEndNew - segmentStartNew)
}

// --- GwpProgressBar ---

/**
 * Label shown under each progress-bar marker.
 *
 * `giftInCart` takes precedence over `cartQualifies`: once a gift is in the cart,
 * `useGwp` drops it from `nextAvailableGiftWithThreshold` (useGwp.ts:60), so the
 * selection carousel unmounts — leaving "Select Below" pointing at nothing.
 *
 * Note `giftInCart` (gwp.ts:53-69) matches on `gift.giftIds` AND the `_bl_gwp`
 * line-item property, so it only ever becomes true when the discount has gift
 * products selected in the Bedford Lane app.
 */
function giftMarkerLabel(gift: ProcessedGiftWithPurchaseObject): string {
  if (gift.giftInCart) return 'Added'
  if (gift.cartQualifies) return gift.giftType === 'shipping' ? 'Applied' : 'Select Below'
  if (gift.minPurchaseType === 'amount') {
    return `Spend ${formatMoney(gift.minPurchaseAmount).replace('.00', '')}`
  }
  return `Add ${gift.minPurchaseQuantity} Item${gift.minPurchaseQuantity > 1 ? 's' : ''}`
}

interface GwpProgressBarProps {
  percentage: number
  gifts: ProcessedGiftWithPurchaseObject[]
  blockSpacing?: boolean
}

export function GwpProgressBar({ percentage, gifts, blockSpacing }: GwpProgressBarProps) {
  const n = gifts?.length ?? 0
  const barWidth = n > 0 ? progressBarWidthForSpaceAround(percentage, n) : 0
  return h(
    'div',
    { className: `${blockSpacing ? 'px-margin lg:px-1.5' : ''} pt-1.5 pb-3.75 border-b border-borders-default` },
    h(
      'div',
      { className: 'relative w-full h-[4px] bg-borders-default' },
      h('div', {
        className: `absolute inset-tl w-gwp-bar-width h-full transition-all ${gifts.some((g) => g.cartQualifies) ? 'bg-type-success' : 'bg-type-black-primary'}`,
        style: { '--gwp-bar-width': `${barWidth}%` } as Record<string, string>
      }),
      gifts.map((gift, index) => {
        const leftPercent = gifts.length > 1 ? ((index + 0.5) / gifts.length) * 100 : 50
        return h(
          'div',
          {
            key: gift.id,
            className: `absolute top-1/2 size-[16px] border-[2px] border-secondary rounded-full transition-all ${
              gift.cartQualifies ? 'bg-type-success' : 'bg-borders-default'
            }`,
            style: { left: `${leftPercent}%`, transform: 'translate(-50%, -50%)' }
          },
          h(
            'div',
            {
              className: `flex flex-col items-center justify-center absolute top-full left-1/2 w-max text-center mt-0.5 -translate-x-1/2 transition-all ${
                gift.cartQualifies ? 'text-type-black-primary' : 'text-type-black-tertiary'
              }`
            },
            h('span', { className: 'paragraph-xs font-medium' }, gift.giftName),
            h('span', { className: 'paragraph-xs' }, giftMarkerLabel(gift))
          )
        )
      })
    )
  )
}

// --- GWP Gift Carousel ---

const GWP_CAROUSEL_OPTIONS = {
  draggable: true,
  freeScroll: false,
  wrapAround: false,
  groupCells: false,
  fade: false,
  adaptiveHeight: false,
  accessibility: true,
  cellAlign: 'left',
  // Arrows are positioned above the cells by the `flickity-nav-inline` class on
  // the wrapper (see lib/third-party.scss). `pageDots` stays off: this theme
  // styles them as a full-width segmented progress line, not dots.
  prevNextButtons: true,
  pageDots: false
}

interface GwpGiftCard {
  key: string
  result: Record<string, unknown>
  priceOverride: { price: number; compareAtPrice: number }
}

interface GwpGiftCarouselProps {
  gift: ProcessedGiftWithPurchaseObject
  cards: GwpGiftCard[]
  addGiftToCart: (variant: { id: string | number }) => Promise<void>
  blockSpacing?: boolean
  loadingState: string
}

/**
 * Renders the gift products inline as a Flickity carousel of ResultUpsellCards
 * (replaces the previous "Select Gift" button + modal flow). Each card owns its
 * own add-to-cart via `options.onAddToCart` so the GWP line-item property and
 * discounted/free price are preserved.
 */
function GwpGiftCarousel({ cards, addGiftToCart, blockSpacing, loadingState }: GwpGiftCarouselProps) {
  const [node, setNode] = useState<Element | null>(null)
  const hasMultiple = cards.length > 1
  const options = { ...GWP_CAROUSEL_OPTIONS, prevNextButtons: hasMultiple }
  const { SliderPortal } = useFlickity(node as Element, options, [node, cards.length])

  return h(
    'div',
    {
      className: `flickity-nav-inline flex flex-col overflow-hidden ${
        blockSpacing ? 'px-margin' : ''
      } py-1 border-b border-borders-default ${loadingState}`
    },
    // Reserves the 52px band the arrows sit in (they are absolutely positioned at
    // top-0 of the carousel, which `flickity-nav-inline` pulls up by the same
    // amount) plus 88px of right padding so the heading clears the two buttons.
    // Mirrors blocks/recommendations-carousel.liquid.
    h(
      'div',
      { className: 'flex w-full items-center pb-1 md:min-h-3.25 md:pb-0.5 md:pr-5.5' },
      h('p', { className: 'paragraph-xs font-serif' }, 'Select your free gift')
    ),
    h(
      'div',
      {
        className:
          'w-full'
      },
      h(
        'div',
        { ref: setNode, className: 'flex w-full' },
        h(
          SliderPortal,
          null,
          ...cards.map((card) =>
            h(
              'div',
              {
                key: card.key,
                className: `${hasMultiple ? 'w-[calc((100%-(0*4px))/1.25)] pr-0.5' : 'w-full'} w-full shrink-0`
              },
              h(ResultUpsellCard, {
                result: card.result,
                className: 'w-full',
                options: {
                  atcText: 'Add To Bag',
                  priceOverride: card.priceOverride,
                  onAddToCart: addGiftToCart
                }
              })
            )
          )
        )
      )
    )
  )
}

// --- GwpGiftItem ---

interface GwpGiftItemProps {
  gift: ProcessedGiftWithPurchaseObject
  onAddToCart?: (opts: { loading?: boolean; cart?: unknown }) => void
  isLoading?: boolean
  blockSpacing?: boolean
}

export function GwpGiftItem({ gift, onAddToCart, isLoading, blockSpacing }: GwpGiftItemProps) {
  const loadingState = isLoading ? 'opacity-50' : ''

  const addGiftToCart = async (variant: { id: string | number }) => {
    try {
      onAddToCart?.({ loading: true })
      await (window as any).Cart.addLineItems(
        { id: variant.id, quantity: 1, properties: { _bl_gwp: String(gift.id).split('/').pop() } },
        { silent: false }
      )
      await (window as any).Cart.getCart()
      onAddToCart?.({ loading: false, cart: (window as any).Cart.value })
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: (error as Error).message
      })
      onAddToCart?.({ loading: false })
      throw error
    }
  }

  if (!gift.customerQualifies) {
    return h(
      'div',
      { className: `flex items-center justify-between gap-0.5 py-0.5 border-b border-borders-default bg-page-secondary ${blockSpacing ? 'px-margin md:px-1.5' : 'px-1'}` },
      h('div', { className: 'paragraph-s font-base font-bold uppercase' }, `Sign up to get a ${gift.giftName}!`),
      h(
        'div',
        { className: 'shrink-0 flex items-center gap-x-0.5' },
        h('a', { href: globalState.routes.account_login_url, className: 'btn btn-text btn-primary btn-s w-fit' },
          h('span', { className: 'text-line text-line-large' }, 'Login / Sign Up')
        )
      )
    )
  }

  const cards: GwpGiftCard[] = gift.giftProducts.map((product) => {
    const featuredImage =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : (product.featured_image as { src?: string })?.src ?? ''

    let discountedPrice = (product.price * (100 - (gift.discountValue as number))) / 100
    const convertedDiscountValue =
      (gift.convertDiscountValue
        ? (gift.discountValue as number) * (window as any).Shopify.currency.rate
        : gift.discountValue) * 100
    switch (gift.discountType) {
      case 'amount':
        discountedPrice = product.price - convertedDiscountValue
        break
      case 'fixed':
        discountedPrice = convertedDiscountValue
        break
    }

    // Adapt the Shopify product object into the shape ResultUpsellCard expects
    // (Athos-style `result`): flat `name`/`handle`/`image`, plus the variants array.
    const result = {
      ...product,
      uid: product.id,
      name: product.title,
      handle: product.handle,
      image: featuredImage,
      // images: undefined forces ResultImage's single-image branch — Shopify
      // product JSON gives images as URL strings, not the { src } objects the
      // two-image branch expects.
      images: undefined,
      is_giftcard: undefined,
      template_suffix: undefined
    }

    return {
      key: String(product.id),
      result: result as unknown as Record<string, unknown>,
      priceOverride: { price: discountedPrice, compareAtPrice: product.price }
    }
  })

  return h(GwpGiftCarousel, { gift, cards, addGiftToCart, blockSpacing, loadingState })
}

// --- GwpCartBlock (full block: progress bar + alert + next gift) ---

interface GwpCartBlockProps {
  cart: any
  freeShippingMinimum: number
  enableProgressBar?: boolean
  handleSetCartConfig?: (key: string, value: unknown) => void
  isLoading?: boolean
  onCartUpdate?: (cart: unknown) => void
  blockSpacing?: boolean
}

export function GwpCartBlock({
  cart,
  freeShippingMinimum,
  enableProgressBar = true,
  handleSetCartConfig,
  isLoading = false,
  onCartUpdate,
  blockSpacing
}: GwpCartBlockProps) {
  const { giftsForProgressBar, giftsProgressPercentage, nextAvailableGiftWithThreshold, invalidGiftItems } = useGwp(
    cart,
    freeShippingMinimum
  )

  const [removedGifts, setRemovedGifts] = useState<any[]>([])
  const [removingGifts, setRemovingGifts] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)

  const effectiveLoading = handleSetCartConfig ? isLoading : localLoading

  useEffect(() => {
    if (invalidGiftItems.length > 0 && !removingGifts) {
      setRemovingGifts(true)
      const removeItems = async () => {
        try {
          if (handleSetCartConfig) {
            handleSetCartConfig(CartConfigKeys.isLoading, true)
          }
          await (window as any).Cart.updateLineItems(
            invalidGiftItems.reduce((acc: any, item: any) => ({ ...acc, [item.key]: 0 }), {}),
            { silent: true }
          )
          setRemovedGifts(invalidGiftItems)
          await (window as any).Cart.getCart()
          const newCart = (window as any).Cart.value
          if (handleSetCartConfig) {
            handleSetCartConfig(CartConfigKeys.cart, newCart)
            handleSetCartConfig(CartConfigKeys.isLoading, false)
          } else if (onCartUpdate) {
            onCartUpdate(newCart)
          }
        } catch (error) {
          globalEmitter.emit(emitterProps.notice.global, { type: 'error', content: (error as Error).message })
          if (handleSetCartConfig) handleSetCartConfig(CartConfigKeys.isLoading, false)
        } finally {
          setRemovingGifts(false)
        }
      }
      removeItems()
    }
  }, [invalidGiftItems])

  const handleGwpAddToCart = ({ loading, cart: newCart }: { loading?: boolean; cart?: unknown }) => {
    if (handleSetCartConfig) {
      if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
      if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
    } else {
      if (loading !== undefined) setLocalLoading(loading)
      if (onCartUpdate && newCart) onCartUpdate(newCart)
    }
  }

  const fragmentChildren: preact.ComponentChildren[] = []
  if (enableProgressBar && giftsForProgressBar?.length > 0) {
    fragmentChildren.push(
      h(GwpProgressBar, { percentage: giftsProgressPercentage, gifts: giftsForProgressBar, blockSpacing })
    )
  }
  if (removedGifts.length > 0) {
    fragmentChildren.push(
      h(
        'div',
        { className: `flex items-center justify-between gap-x-0.75 bg-page-error py-px border-b border-borders-default ${blockSpacing ? 'pl-margin md:pl-1.5 md:pr-0.5' : 'pl-1'}` },
        h(
          'p',
          { className: 'paragraph-xs font-medium' },
          `You no longer qualify for ${
            removedGifts.length > 1
              ? 'some free gifts and they have been removed'
              : 'a free gift and it has been removed'
          }.`
        ),
        h(
          'button',
          {
            type: 'button',
            className: 'btn btn-transparent btn-primary btn-m btn-square',
            'aria-label': 'Close',
            onClick: () => setRemovedGifts([])
          },
          h(
            'div',
            { className: 'size-[9px] children:size-full' },
            h(Icon, { id: 'close' })
          )
        )
      )
    )
  }
  if (nextAvailableGiftWithThreshold) {
    fragmentChildren.push(
      h(GwpGiftItem, {
        gift: nextAvailableGiftWithThreshold,
        onAddToCart: handleGwpAddToCart,
        isLoading: effectiveLoading,
        blockSpacing
      })
    )
  }

  return h(Fragment, null, ...fragmentChildren)
}

// --- Custom element (cart page mount) ---

interface CartLike {
  items?: unknown[]
  item_count?: number
  [key: string]: unknown
}

const CART_EVENTS = ['renderCartPage', 'renderMiniCart']

function GwpCartBlockContainer() {
  const initCart = (window as any).Cart?.value ?? (globalState as any)?.cart?.initCart ?? null
  const [cart, setCart] = useState<CartLike | null>(initCart)
  const freeShippingMinimum = (globalState as any)?.cart?.freeShippingMinimum ?? 0

  const refreshCart = useCallback(() => {
    if (!(window as any).Cart) return
    ;(window as any).Cart.getCart().then(() => {
      if ((window as any).Cart?.value) {
        setCart({ ...(window as any).Cart.value })
      }
    })
  }, [])

  useEffect(() => {
    if (!cart) refreshCart()

    const handler = () => refreshCart()
    CART_EVENTS.forEach((event) => document.addEventListener(event, handler))
    return () => {
      CART_EVENTS.forEach((event) => document.removeEventListener(event, handler))
    }
  }, [])

  if (!cart) return null

  return h(GwpCartBlock, {
    cart,
    freeShippingMinimum,
    enableProgressBar: (globalState as any)?.cart?.gwp?.enableProgressBar,
    onCartUpdate: (newCart: unknown) => setCart(newCart as CartLike | null)
  })
}

class GwpCartBlockElement extends HTMLElement {
  connectedCallback() {
    this.renderComponent()
  }

  disconnectedCallback() {
    render(null, this)
  }

  private renderComponent() {
    render(h(GwpCartBlockContainer, null), this)
  }
}

export { GwpCartBlockElement }
